import cn from 'clsx';
import React, { forwardRef } from 'react';

import { BaseAsType, PolymorphicRef, PropsWithAs } from '../__utils__/withAsProp';
import T from '../Typography';
import ProgressSpinner from '../ProgressSpinner';
import type { ButtonBaseProps, ButtonPropsWithAs, ButtonPropsWithoutAs, ButtonType } from './Button.types';
import { getSizingProp } from '../CrescentCss/CrescentCss';
import { LoadingOverlay } from './Button.styles';

namespace Button {
    export type Props = React.ComponentPropsWithRef<ButtonType>;
    export type PropsWithoutAs = ButtonPropsWithoutAs; // Without the "as" prop for easy type inference. Should be 99% of the use case.
    export type PropsWithAs<As extends BaseAsType = 'button'> = ButtonPropsWithAs<As>; // Actual prop with as.
}

const Button = forwardRef(function ButtonWithRef<As extends BaseAsType = 'button'>(
    {
        as: asProp,
        children,
        className,
        disabled,
        icon,
        iconPlacement = 'start',
        type,
        kind = 'secondary',
        compact,
        loading = false,
        style,
        ...restProps
    }: PropsWithAs<ButtonBaseProps, As>,
    ref?: PolymorphicRef<As>
) {
    const hasIcon = Boolean(icon);
    const isIconOnly = hasIcon && !children;
    const isLoading = Boolean(loading);

    const RootElement = asProp || 'button';

    return (
        <RootElement
            className={cn('bbui-button', 'cds-button', `cds-button--${kind}`, className, {
                'cds-disabled': disabled || isLoading,
                'cds-button--icon': isIconOnly,
            })}
            disabled={disabled || isLoading}
            type={!asProp && !type ? 'button' : type || undefined}
            ref={ref}
            style={{
                ...(isLoading && { position: 'relative' }),
                ...(style ?? {}),
            }}
            {...getSizingProp(compact)}
            {...restProps}
        >
            {hasIcon && iconPlacement === 'start' && icon}
            {children && <T.ButtonLabel className="bbui-button-contents">{children}</T.ButtonLabel>}

            {isLoading && (
                <LoadingOverlay>
                    <ProgressSpinner size="small" label="" />
                </LoadingOverlay>
            )}
            {hasIcon && iconPlacement === 'end' && icon}
        </RootElement>
    );
}) as ButtonType;

Button.displayName = 'Button';

// Docusaurus somehow can't pick up the default exported component with polymorphic type.
/**
 * @component
 * @visibleName Button
 */
export const DocusaurusButton = Button;

export default Button;
